// =============================================
// Offline write outbox
// A durable queue of pending writes. When a write fails because we're offline,
// it's parked here and the UI keeps working; on reconnect the queue drains to
// the API in order. The outbox is a TRANSIENT queue that empties into the
// server — NOT a parallel source of truth (so no silent divergence).
//
// v1 stores ops in localStorage (fine for small JSON writes). For large document
// blobs, move to IndexedDB — see go-live notes.
// =============================================
import { insertRow, updateRow } from '../features/db-viewer/data/dbViewerService';

export interface OutboxOp {
  id: string;
  table: string;
  type: 'insert' | 'update';          // delete is modeled as an update (soft-delete payload)
  rowId?: number | string;            // for update
  payload: Record<string, any>;
  label?: string;                     // human-readable, for the sync UI
  createdAt: string;
  tries: number;
  lastError?: string;
}

const KEY = 'akt_outbox';
const listeners = new Set<() => void>();

function notify() { listeners.forEach(l => l()); }
function read(): OutboxOp[] {
  try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch { return []; }
}
function write(ops: OutboxOp[]) {
  localStorage.setItem(KEY, JSON.stringify(ops));
  notify();
}

let _seq = 0;
function genId(): string {
  return `op_${Date.now().toString(36)}_${(_seq++).toString(36)}`;
}

export function enqueue(op: Omit<OutboxOp, 'id' | 'createdAt' | 'tries'>): OutboxOp {
  const full: OutboxOp = { ...op, id: genId(), createdAt: new Date().toISOString(), tries: 0 };
  const ops = read();
  ops.push(full);
  write(ops);
  return full;
}

export function pendingCount(): number { return read().length; }
export function getAll(): OutboxOp[] { return read(); }
export function clearOutbox(): void { write([]); }
export function onOutboxChange(cb: () => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

/** A failure caused by being offline (vs a real server-side/validation error). */
export function isNetworkError(err: any): boolean {
  return err instanceof TypeError ||
    /failed to fetch|networkerror|load failed|network request failed/i.test(err?.message || '');
}

let _flushing = false;

/** Drain the queue to the API in order. Stops at the first network error
 * (still offline); server errors are flagged and skipped so they don't block others. */
export async function flushOutbox(): Promise<{ ok: number; failed: number; remaining: number }> {
  if (_flushing) return { ok: 0, failed: 0, remaining: pendingCount() };
  _flushing = true;
  let ok = 0, failed = 0;
  try {
    for (const op of read()) {
      try {
        if (op.type === 'insert') await insertRow(op.table, op.payload);
        else await updateRow(op.table, op.rowId as any, op.payload);
        write(read().filter(o => o.id !== op.id));   // success → remove
        ok++;
      } catch (err: any) {
        if (isNetworkError(err)) break;              // still offline → stop, keep the rest
        write(read().map(o => o.id === op.id ? { ...o, tries: o.tries + 1, lastError: err?.message || 'error' } : o));
        failed++;                                     // server error → flag, keep, continue
      }
    }
  } finally {
    _flushing = false;
  }
  return { ok, failed, remaining: pendingCount() };
}
